// Single source of truth for SEO metadata + JSON-LD, shared by the client
// (`src/components/seo.tsx`, `src/components/route-seo.tsx`, and the detail
// pages) AND the production Node HTML server (`server/index.ts`). It must stay
// PURE: no `window`, no `import.meta.env`, no React. The only value imports are
// the pure label/route helpers; everything else is type-only.

import type {
  Airport,
  Fbo,
  FlightSchoolDetail,
  MaintenanceProviderDetail,
  Provider,
  Event,
  CmsPage,
  PolicyDocumentDetail,
} from "@workspace/api-client-react";
import {
  airportDetailPath,
  experienceDetailPath,
  fboDetailPath,
  flightSchoolDetailPath,
  maintenanceDetailPath,
  providerDetailPath,
} from "@workspace/detail-routes";
import {
  US_STATES,
  stateNameToSlug,
  usStateFromSlug,
} from "@workspace/forms-spec";
import { categoryLabel, coverageSummary } from "@/lib/providers";
import { cmsPageRealRoute } from "@/lib/cms-routes";

export const SITE_NAME = "AERO";

export const DEFAULT_DESCRIPTION =
  "AERO connects the aviation ecosystem — from FBOs and flight schools to hangars, maintenance, services, and experiences — in one seamless discovery platform.";

export type JsonLd = Record<string, unknown>;

/** The platform-neutral metadata for a route. Mirrors `SeoProps`. */
export interface MetaInput {
  title: string;
  description?: string;
  /** Path relative to app root for canonical / og:url (e.g. "/fbos/abc"). */
  canonicalPath?: string;
  /** Absolute URL or a public path (e.g. "/opengraph.jpg"). */
  image?: string | null;
  noindex?: boolean;
  jsonLd?: JsonLd | JsonLd[];
  /** Append " | AERO" unless the title already contains the site name. */
  appendSiteName?: boolean;
}

/**
 * Environment-specific helpers a meta builder needs. The client supplies
 * window/Vite-backed versions; the server supplies request-derived versions.
 */
export interface SeoContext {
  /** Absolute URL builder (origin + base path + path). */
  abs: (path?: string) => string;
  /** Resolve a possibly-relative public image path to a base-prefixed URL. */
  resolveImg: (url: string) => string;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Build an absolute URL for a path relative to the app root. An already-absolute
 * `http(s)` URL is returned unchanged so callers can pass a full canonical/OG
 * URL without it being double-prefixed.
 */
export function makeAbsoluteUrl(
  origin: string,
  basePath: string,
  path?: string,
): string {
  const base = basePath.replace(/\/$/, "");
  if (!path) return `${origin}${base}/`;
  if (/^https?:\/\//i.test(path)) return path;
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${origin}${base}${clean === "/" ? "/" : clean}`;
}

/**
 * Resolve a public asset path the way the client's `resolveImageUrl` does:
 * absolute/data URLs pass through, relative paths get the base-path prefix.
 */
export function resolveImageUrlWith(url: string, basePath: string): string {
  if (/^https?:\/\//.test(url) || url.startsWith("data:")) return url;
  const base = basePath.endsWith("/") ? basePath : `${basePath}/`;
  return `${base}${url.replace(/^\//, "")}`;
}

/** Trim a string to a sensible meta-description length on a word boundary. */
export function clip(text: string, max = 158): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (normalized.length <= max) return normalized;
  const slice = normalized.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(" ");
  return `${(lastSpace > 40 ? slice.slice(0, lastSpace) : slice).trimEnd()}…`;
}

/**
 * Serialize JSON-LD for safe inline injection inside a <script> tag. Neutralizes
 * `</script>` and the JS line separators U+2028/U+2029 to prevent script
 * breakout if a data-derived field ever contains those sequences.
 */
export function safeJsonLd(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

// ---------------------------------------------------------------------------
// resolveHead — turns a MetaInput into the concrete head field values. Used by
// BOTH the client <Seo> component and the server head renderer, so they can
// never drift.
// ---------------------------------------------------------------------------

export interface ResolvedHead {
  fullTitle: string;
  description: string;
  canonical: string;
  ogImage: string;
  ogType: string;
  robots: string;
  jsonLdBlocks: JsonLd[];
}

export interface ResolveHeadEnv {
  origin: string;
  basePath: string;
  /** Fallback canonical when no canonicalPath is given (client: current href). */
  currentUrl?: string;
}

/**
 * Apply the site-name suffix exactly as `resolveHead` does, so a portal preview
 * can show the same `<title>` a crawler will see. Idempotent when the title
 * already contains the site name.
 */
export function withSiteName(title: string, append = true): string {
  return !append || title.includes(SITE_NAME)
    ? title
    : `${title} | ${SITE_NAME}`;
}

export function resolveHead(meta: MetaInput, env: ResolveHeadEnv): ResolvedHead {
  const abs = (p?: string) => makeAbsoluteUrl(env.origin, env.basePath, p);
  const fullTitle = withSiteName(meta.title, meta.appendSiteName ?? true);

  const description = meta.description ?? DEFAULT_DESCRIPTION;

  const canonical = meta.canonicalPath
    ? abs(meta.canonicalPath)
    : (env.currentUrl ?? abs());

  const ogImage = meta.image
    ? meta.image.startsWith("http")
      ? meta.image
      : abs(meta.image)
    : abs("/opengraph.jpg");

  const robots = meta.noindex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large";

  const jsonLdBlocks = meta.jsonLd
    ? Array.isArray(meta.jsonLd)
      ? meta.jsonLd
      : [meta.jsonLd]
    : [];

  return {
    fullTitle,
    description,
    canonical,
    ogImage,
    ogType: "website",
    robots,
    jsonLdBlocks,
  };
}

// ---------------------------------------------------------------------------
// Static route metadata maps (mirrors the legacy route-seo.tsx tables).
// ---------------------------------------------------------------------------

interface PublicPageMeta {
  title: string;
  description: string;
}

/** Public pages that should be indexed, with their own title + description. */
export const PUBLIC_PAGES: Record<string, PublicPageMeta> = {
  "/": {
    title: "AERO — Aviation, connected.",
    description: DEFAULT_DESCRIPTION,
  },
  "/airports": {
    title: "Browse Business Aviation Airports",
    description:
      "Search US business aviation airports. Compare runways, FBOs, and the lowest Jet A fuel prices across the network.",
  },
  "/fbos": {
    title: "Browse FBOs & Compare Jet A Fuel Prices",
    description:
      "Browse and filter US FBOs by amenities, services, and Jet A fuel pricing. Compare facilities side by side and read reviews from the flight community.",
  },
  "/flight-schools": {
    title: "Browse Flight Schools & Training",
    description:
      "Find US flight schools and training providers. Compare programs, aircraft, ratings, and facilities across the business aviation network.",
  },
  "/aircraft-sales/dealers": { title: "Aircraft Dealers & Brokers", description: "Find aircraft sales, brokerage and acquisition specialists on AERO." },
  "/aircraft-sales": {
    title: "Aircraft for Sale, Dealers & Brokers",
    description: "Search aircraft for sale by make, model, year, price and airframe hours. Connect with aircraft dealers, brokers and acquisition specialists.",
  },
  "/services": {
    title: "Aviation Service Providers",
    description:
      "Browse business aviation service providers — handling, catering, detailing, and more — across US airports and FBOs.",
  },
  "/maintenance": {
    title: "Aircraft Maintenance Providers",
    description:
      "Find aircraft maintenance, repair, and overhaul providers across the US business aviation network.",
  },
  "/hangar": {
    title: "Hangar Space & Availability",
    description:
      "Find hangar space, availability, and inventory across US business aviation airports and FBOs.",
  },
  "/experiences": {
    title: "Aviation Experiences & Event Operations",
    description:
      "Operations guides for major business aviation events — peak arrival windows, FBO availability, fuel pricing, and ground logistics.",
  },
  "/map": {
    title: "Network Map of FBOs & Airports",
    description:
      "Explore the AERO network on an interactive map of US business aviation airports and FBOs.",
  },
  "/plans": {
    title: "Plans & Pricing",
    description:
      "Compare AERO membership plans for FBOs and facility owners — listing placement, analytics, and lead tools.",
  },
  "/advertise": {
    title: "Advertise on AERO",
    description:
      "Reach business aviation decision-makers. Promote your brand across the AERO network of airports and FBOs.",
  },
  "/get-listed": {
    title: "Get Your FBO Listed",
    description:
      "Add or claim your FBO on AERO to reach pilots and flight departments searching for fuel, services, and handling.",
  },
  "/privacy": {
    title: "Privacy Center",
    description:
      "Review AERO's privacy policies and notices, manage your privacy choices, and exercise your data rights.",
  },
  "/trust": {
    title: "Trust Center",
    description:
      "How AERO protects data, secures its infrastructure, and respects the rights of the aviation community.",
  },
  "/support": {
    title: "Support & Contact",
    description:
      "Contact the AERO team — email and phone support for pilots, the mobile app, accounts, and facility listings, with expected response times.",
  },
  "/status": {
    title: "System Status",
    description:
      "Live operational status and uptime history for the AERO platform, updated automatically from continuous health monitoring.",
  },
  "/whats-new": {
    title: "What's New",
    description:
      "The latest features, improvements, and fixes shipped to the AERO business aviation platform.",
  },
  "/suggestions": {
    title: "Suggestion Board",
    description:
      "Share ideas for the AERO platform and vote on what the community wants next.",
  },
  "/in-progress": {
    title: "In Progress",
    description:
      "Features and improvements actively under development on the AERO platform. Details may change as we build.",
  },
  "/compare": {
    title: "Compare FBOs Side By Side",
    description:
      "Compare up to 4 US FBOs side by side — Jet A and Avgas fuel prices, AERO Score, ratings, reviews, amenities, and services. Build a shareable FBO comparison link.",
  },
  "/roadmap": {
    title: "Product Roadmap",
    description:
      "A forward look at what's planned for the AERO business aviation platform. Timing and scope are directional.",
  },
};

/** Authenticated / utility pages that should never be indexed. */
export const PRIVATE_PAGES: Record<string, string> = {
  "/operator": "Operator Workspace",
  "/fbo-ops": "FBO Operations",
  "/portal/operator": "Operator Workspace",
  "/portal/operator/schedule": "Operator Schedule",
  "/portal/operator/fleet": "Operator Fleet",
  "/portal/operator/requests": "Operator Service Requests",
  "/portal/operator/trips": "Operator Trips",
  "/service-requests": "Service Requests",
  "/account": "Your Account",
  "/favorites": "Saved Facilities",
  "/notifications": "Notifications",
  "/portal": "Business Console",
  "/portal/sales": "Aircraft Sales Console",
  "/portal/plans": "Listing Plans Console",
  // Auth-gated plan builder (signed-out visitors see a lock screen), so it has a
  // proper title but stays out of the index rather than falling to "Page Not Found".
  "/build-plan": "Build Your Custom Plan",
};

/** Valid but non-indexed dynamic routes (so they aren't mislabeled "Page Not Found"). */
export const PRIVATE_PREFIXES: { re: RegExp; title: string }[] = [
  { re: /^\/portal\/operator\/companies\/[^/]+$/, title: "Company Operator Workspace" },
  { re: /^\/(?:portal\/)?operator\/requests\/[^/]+$/, title: "Operator Service Request" },
  { re: /^\/(?:portal\/)?operator\/trips\/[^/]+$/, title: "Operator Trip" },
  {
    re: /^\/portal\/(?:school\/[^/]+|provider\/[^/]+|maintenance\/[^/]+|[^/]+)$/,
    title: "Facility Portal",
  },
  { re: /^\/quote\/[^/]+$/, title: "Service Quote" },
];

/**
 * Client-rendered routes that are valid application pages but are intentionally
 * absent from the public sitemap. Keeping them separate from `PRIVATE_PAGES`
 * avoids treating public token/utility screens as authenticated account pages,
 * while still giving the production HTML server a real 200 + noindex response.
 */
export const NOINDEX_PAGES: Record<string, string> = {
  "/sign-in": "Sign In",
  "/sign-up": "Create Your Account",
  "/accept-invite": "Accept Invitation",
  "/tester-invite": "Tester Invitation",
  "/fleet": "Your Fleet",
  "/messages": "Messages",
  "/pilots": "Pilot Directory",
  "/portal/fbo-ops": "AERO FBO Tool",
  "/portal/fbo-ops/requests": "FBO Service Requests",
  "/portal/fbo-ops/requests/new": "New FBO Service Request",
  "/portal/fbo-ops/ramp": "Ramp Operations",
  "/portal/fbo-ops/schedule": "FBO Schedule",
  "/portal/fbo-ops/messages": "FBO Messages",
  "/portal/fbo-ops/hangar": "Hangar Operations",
  "/portal/fbo-ops/reports": "FBO Reports",
  "/portal/fbo-ops/reports/fuel": "Fuel Report",
  "/portal/fbo-ops/settings": "FBO Tool Settings",
  "/lite": "AERO Lite",
  "/lite/trip": "Trip Planner",
  "/lite/trips": "My Trips",
  "/lite/requests": "My Requests",
  "/lite/messages": "Messages",
  "/display/pair": "Pair Display",
  "/display/unauthorized": "Display Access Required",
  "/display/offline": "Display Offline",
};

/** Valid parameterized utility routes, matched exactly rather than by broad prefix. */
export const NOINDEX_PREFIXES: { re: RegExp; title: string }[] = [
  // Public entity visibility is checked by the marketplace API; SSR metadata is pending.
  { re: /^\/aircraft-sales\/aircraft\/[^/]+$/, title: "Aircraft for Sale" },
  { re: /^\/sign-in(?:\/.*)?$/, title: "Sign In" },
  { re: /^\/sign-up(?:\/.*)?$/, title: "Create Your Account" },
  { re: /^\/weather\/[^/]+$/, title: "Airport Weather" },
  { re: /^\/assist\/a\/[^/]+$/, title: "Shared AERO Assist Answer" },
  { re: /^\/service-requests\/[^/]+$/, title: "Service Request" },
  { re: /^\/hangar\/request\/[^/]+$/, title: "Hangar Request" },
  { re: /^\/hangar\/waitlist\/[^/]+$/, title: "Hangar Waitlist" },
  { re: /^\/partner\/[^/]+$/, title: "Partner Agreement" },
  { re: /^\/admin(?:\/.*)?$/, title: "AERO Administration" },
  { re: /^\/support\/[^/]+$/, title: "Support Case" },
  { re: /^\/(?:portal\/)?fbo-ops\/requests\/[^/]+$/, title: "FBO Service Request" },
  { re: /^\/(?:portal\/)?fbo-ops\/visits\/[^/]+$/, title: "FBO Visit" },
  { re: /^\/lite\/airport\/[^/]+$/, title: "Airport — AERO Lite" },
  {
    re: /^\/lite\/fbo\/[^/]+\/[^/]+$/,
    title: "FBO — AERO Lite",
  },
  { re: /^\/lite\/trips\/shared\/[^/]+$/, title: "Shared Trip" },
  { re: /^\/lite\/messages\/[^/]+$/, title: "Trip Messages" },
  { re: /^\/lite\/trips\/[^/]+$/, title: "Trip" },
  {
    re: /^\/display\/[^/]+\/[^/]+$/,
    title: "AERO Operations Display",
  },
  { re: /^\/play\/[^/]+$/, title: "Event Experience" },
  { re: /^\/join\/[^/]+$/, title: "Join Event" },
];

// ---------------------------------------------------------------------------
// Programmatic SEO: per-state Jet A fuel price pages (`/fuel-prices/:state`)
// plus their index (`/fuel-prices`). These are public, indexable pages whose
// bodies render live FBO data client-side; the server emits an indexable head
// from these pure helpers (no data fetch needed for the title/description), and
// the page component reuses the SAME helpers for its own <Seo> so the two can
// never drift.
// ---------------------------------------------------------------------------

// Slug helpers are shared with the api-server OG share-card route via
// @workspace/forms-spec (next to US_STATES) so the two sides can never drift.
export { stateNameToSlug } from "@workspace/forms-spec";

/**
 * Resolve a `/fuel-prices/:state` slug (either a state-name slug like "texas"
 * or a two-letter code like "tx") to its canonical code + name, or null.
 */
export const fuelStateFromSlug = usStateFromSlug;

/** Meta for the `/fuel-prices` index page. */
export const FUEL_PRICES_INDEX_META: PublicPageMeta = {
  title: "Jet A Fuel Prices By State",
  description:
    "Compare Jet A fuel prices at FBOs across every US state. Find the lowest and average Jet A pricing state by state on the AERO business aviation network.",
};

/** Meta for a `/fuel-prices/:state` page, given the resolved state name. */
export function fuelPricesStateMeta(name: string): PublicPageMeta {
  return {
    title: `Jet A Prices In ${name}`,
    description: `Compare Jet A fuel prices at FBOs in ${name}. See the lowest and average Jet A pricing and find the cheapest fuel across ${name} business aviation airports.`,
  };
}

/**
 * Meta for the `/avgas-prices` index page. The Avgas pages are a dedicated
 * crawlable route family (not a `?fuel=` variant of `/fuel-prices`) so piston
 * searches like "100LL prices in Texas" resolve to a URL whose canonical,
 * title, description, and JSON-LD are all Avgas-first.
 */
export const AVGAS_PRICES_INDEX_META: PublicPageMeta = {
  title: "Avgas 100LL Fuel Prices By State",
  description:
    "Compare Avgas (100LL) fuel prices at FBOs across every US state. Find the lowest and average 100LL pricing state by state on the AERO aviation network.",
};

/** Meta for an `/avgas-prices/:state` page, given the resolved state name. */
export function avgasPricesStateMeta(name: string): PublicPageMeta {
  return {
    title: `Avgas 100LL Prices In ${name}`,
    description: `Compare Avgas (100LL) fuel prices at FBOs in ${name}. See the lowest and average 100LL pricing and find the cheapest avgas across ${name} airports.`,
  };
}

/** Organization + WebSite structured data for the home page. */
export function homeJsonLd(abs: (path?: string) => string): JsonLd[] {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: abs("/"),
      logo: abs("/logo.svg"),
      description: DEFAULT_DESCRIPTION,
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: abs("/"),
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${abs("/fbos")}?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    },
  ];
}

// ---------------------------------------------------------------------------
// Shareable comparison links (`/compare?fbos=a,b`). The OG image varies by the
// `fbos` query param so a pasted comparison link unfurls with a card naming the
// compared FBOs, while the canonical stays the bare `/compare`.
// ---------------------------------------------------------------------------

/** Mirrors `MAX_COMPARE` in `@/lib/compare` (kept here so this module stays pure). */
export const COMPARE_MAX_SLUGS = 4;

/**
 * Parse a `?fbos=slug-a,slug-b` comparison query string. Slugs are sanitized
 * to the URL-slug charset, deduped, and capped so a crafted URL can never fan
 * out unbounded fetches. Shared by the compare page, the client RouteSeo, and
 * the server head renderer so they can never drift.
 */
export function parseCompareSlugs(
  search: string,
  max = COMPARE_MAX_SLUGS,
): string[] {
  const raw = new URLSearchParams(search).get("fbos");
  if (!raw) return [];
  const out: string[] = [];
  for (const part of raw.split(",")) {
    const slug = part.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]*$/.test(slug)) continue;
    if (out.includes(slug)) continue;
    out.push(slug);
    if (out.length >= max) break;
  }
  return out;
}

/** Dynamic OG card path for a comparison selection (api-server `/og/compare`). */
export function compareOgImagePath(slugs: string[]): string {
  return `/api/og/compare?fbos=${encodeURIComponent(slugs.join(","))}`;
}

/** Dynamic OG card path for a state fuel-prices page (api-server `/og/fuel-prices/:state`). */
export function fuelPricesOgImagePath(stateSlug: string): string {
  return `/api/og/fuel-prices/${stateSlug}`;
}

/** Dynamic OG card path for the national fuel-prices index (api-server `/og/fuel-prices`). */
export function fuelPricesIndexOgImagePath(): string {
  return "/api/og/fuel-prices";
}

/** Dynamic OG card path for a state Avgas-prices page (api-server `/og/avgas-prices/:state`). */
export function avgasPricesOgImagePath(stateSlug: string): string {
  return `/api/og/avgas-prices/${stateSlug}`;
}

/** Dynamic OG card path for the national Avgas-prices index (api-server `/og/avgas-prices`). */
export function avgasPricesIndexOgImagePath(): string {
  return "/api/og/avgas-prices";
}

/**
 * Compute metadata for a non-detail (static / private / unknown) path. Mirrors
 * the legacy RouteSeo precedence exactly. `search` (the raw query string) only
 * affects routes whose OG image varies by query param (currently `/compare`).
 */
export function resolveStaticMeta(
  path: string,
  ctx: SeoContext,
  search?: string,
): MetaInput {
  const pub = PUBLIC_PAGES[path];
  if (pub) {
    const compareSlugs =
      path === "/compare" && search ? parseCompareSlugs(search) : [];
    return {
      title: pub.title,
      description: pub.description,
      canonicalPath: path,
      appendSiteName: path !== "/",
      jsonLd: path === "/" ? homeJsonLd(ctx.abs) : undefined,
      image: compareSlugs.length
        ? compareOgImagePath(compareSlugs)
        : undefined,
    };
  }
  const priv = PRIVATE_PAGES[path];
  if (priv) {
    return { title: priv, noindex: true, canonicalPath: path };
  }
  const prefix = PRIVATE_PREFIXES.find((p) => p.re.test(path));
  if (prefix) {
    return { title: prefix.title, noindex: true, canonicalPath: path };
  }
  const noindex = NOINDEX_PAGES[path];
  if (noindex) {
    return { title: noindex, noindex: true, canonicalPath: path };
  }
  const noindexPrefix = NOINDEX_PREFIXES.find((p) => p.re.test(path));
  if (noindexPrefix) {
    return {
      title: noindexPrefix.title,
      noindex: true,
      canonicalPath: path,
    };
  }
  if (path === "/fuel-prices") {
    return {
      title: FUEL_PRICES_INDEX_META.title,
      description: FUEL_PRICES_INDEX_META.description,
      canonicalPath: path,
      image: fuelPricesIndexOgImagePath(),
    };
  }
  const fuelState = path.match(/^\/fuel-prices\/([^/]+)$/);
  if (fuelState) {
    const st = fuelStateFromSlug(decodeURIComponent(fuelState[1]));
    if (st) {
      const m = fuelPricesStateMeta(st.name);
      const slug = stateNameToSlug(st.name);
      return {
        title: m.title,
        description: m.description,
        canonicalPath: `/fuel-prices/${slug}`,
        image: fuelPricesOgImagePath(slug),
      };
    }
  }
  if (path === "/avgas-prices") {
    return {
      title: AVGAS_PRICES_INDEX_META.title,
      description: AVGAS_PRICES_INDEX_META.description,
      canonicalPath: path,
      image: avgasPricesIndexOgImagePath(),
    };
  }
  const avgasState = path.match(/^\/avgas-prices\/([^/]+)$/);
  if (avgasState) {
    const st = fuelStateFromSlug(decodeURIComponent(avgasState[1]));
    if (st) {
      const m = avgasPricesStateMeta(st.name);
      const slug = stateNameToSlug(st.name);
      return {
        title: m.title,
        description: m.description,
        canonicalPath: `/avgas-prices/${slug}`,
        image: avgasPricesOgImagePath(slug),
      };
    }
  }
  return { title: "Page Not Found", noindex: true };
}

// ---------------------------------------------------------------------------
// Detail-route classification. Self-managed detail pages render their own meta
// from live entity data (client) / are fetched + injected by the server.
// ---------------------------------------------------------------------------

export type DetailRoute =
  | { type: "airport"; code: string }
  | { type: "fbo"; airportCode: string; nameSlug: string }
  | { type: "flightSchool"; airportCode: string; nameSlug: string }
  | { type: "maintenance"; airportCode: string; nameSlug: string }
  | { type: "provider"; slug: string }
  | { type: "experience"; slug: string }
  | { type: "cms"; slug: string }
  | { type: "privacy"; slug: string };

const SEG = "[^/]+";

/** Classify a path as a self-managed detail route, or null. */
export function matchDetailRoute(path: string): DetailRoute | null {
  let m: RegExpMatchArray | null;

  m = path.match(new RegExp(`^/airports/(${SEG})$`));
  if (m) return { type: "airport", code: decodeURIComponent(m[1]) };

  m = path.match(new RegExp(`^/fbos/(${SEG})/(${SEG})$`));
  if (m)
    return {
      type: "fbo",
      airportCode: decodeURIComponent(m[1]),
      nameSlug: decodeURIComponent(m[2]),
    };

  m = path.match(new RegExp(`^/flight-schools/(${SEG})/(${SEG})$`));
  if (m)
    return {
      type: "flightSchool",
      airportCode: decodeURIComponent(m[1]),
      nameSlug: decodeURIComponent(m[2]),
    };

  m = path.match(new RegExp(`^/maintenance/(${SEG})/(${SEG})$`));
  if (m)
    return {
      type: "maintenance",
      airportCode: decodeURIComponent(m[1]),
      nameSlug: decodeURIComponent(m[2]),
    };

  m = path.match(new RegExp(`^/services/(${SEG})$`));
  if (m) return { type: "provider", slug: decodeURIComponent(m[1]) };

  m = path.match(new RegExp(`^/experiences/(${SEG})$`));
  if (m) return { type: "experience", slug: decodeURIComponent(m[1]) };

  m = path.match(new RegExp(`^/p/(${SEG})$`));
  if (m) return { type: "cms", slug: decodeURIComponent(m[1]) };

  m = path.match(new RegExp(`^/privacy/(${SEG})$`));
  if (m) return { type: "privacy", slug: decodeURIComponent(m[1]) };

  return null;
}

/**
 * A legacy single-segment detail URL that the SPA client-redirects to its
 * canonical two-segment form (e.g. `/fbos/<globalSlug>` ->
 * `/fbos/<airportCode>/<nameSlug>`). The production head server resolves these
 * server-side and emits a real 301 so crawlers/unfurlers follow the link
 * instead of seeing the no-JS 404 shell.
 */
export type LegacyDetailRoute =
  | { type: "fbo"; slug: string }
  | { type: "flightSchool"; slug: string }
  | { type: "maintenance"; slug: string };

/**
 * Classify a path as a legacy single-segment detail route, or null. Only the
 * three two-segment-canonical families (FBO / flight school / maintenance) have
 * a single-segment legacy form; everything else (airports, services,
 * experiences, CMS) is already single-segment canonical, so it is never matched
 * here (and is matched by `matchDetailRoute` instead).
 */
export function matchLegacyDetailRoute(path: string): LegacyDetailRoute | null {
  let m: RegExpMatchArray | null;

  m = path.match(new RegExp(`^/fbos/(${SEG})$`));
  if (m) return { type: "fbo", slug: decodeURIComponent(m[1]) };

  m = path.match(new RegExp(`^/flight-schools/(${SEG})$`));
  if (m) return { type: "flightSchool", slug: decodeURIComponent(m[1]) };

  m = path.match(new RegExp(`^/maintenance/(${SEG})$`));
  if (m) return { type: "maintenance", slug: decodeURIComponent(m[1]) };

  return null;
}

/**
 * Path prefixes for the self-managed detail families. Broader than
 * `matchDetailRoute` (which is exact, for the server's entity fetch): the client
 * RouteSeo must stay silent for any path under a detail family — including
 * legacy single-segment URLs that client-redirect (e.g. `/fbos/<slug>`) — so it
 * never paints a stray "Page Not Found" over a detail page's own <Seo>.
 */
const SELF_MANAGED_PREFIXES: RegExp[] = [
  /^\/airports\/.+/,
  /^\/fbos\/.+/,
  /^\/flight-schools\/.+/,
  /^\/maintenance\/.+/,
  /^\/services\/.+/,
  /^\/experiences\/.+/,
  /^\/p\/.+/,
  // Published privacy-policy documents (`/privacy/<slug>`) render their own
  // <Seo> from live policy data; the `/privacy` index itself stays static.
  /^\/privacy\/.+/,
  // Weather pages emit data-derived, noindex metadata from the page itself.
  /^\/weather\/[^/]+$/,
  // Programmatic SEO fuel-price pages render their own <Seo> (dynamic title +
  // ItemList JSON-LD from live FBO data). Cover the index and per-state paths
  // for both fuel-family route sets (Jet A and Avgas 100LL).
  /^\/fuel-prices(\/.+)?$/,
  /^\/avgas-prices(\/.+)?$/,
];

/**
 * True when a detail page owns its own <Seo> (so the global RouteSeo must NOT
 * also render one). Prefix-based so it also covers legacy/sub paths under a
 * detail family, not just the exact canonical URLs `matchDetailRoute` accepts.
 */
export function isSelfManagedPath(path: string): boolean {
  return SELF_MANAGED_PREFIXES.some((re) => re.test(path));
}

// ---------------------------------------------------------------------------
// Entity meta builders. Each returns a MetaInput equivalent to the inline
// <Seo> the detail page used to render.
//
// HYDRATION-CRITICAL: the JSON-LD <script> blocks render inline in the BODY on
// BOTH the server (renderToString, no `window` → origin "") and the client
// (real `window.location.origin`). Any origin-derived value inside a JSON-LD
// block therefore differs across the two renders and trips a React hydration
// mismatch. So in-body JSON-LD `url` fields use the root-relative `canonicalPath`
// (deterministic on both sides; schema.org resolves it against the page URL) —
// never `ctx.abs(...)`. The hoistable head fields (og:image, canonical, og:url)
// stay absolute via `ctx.abs(...)` because they are rendered client-only and so
// never participate in hydration. See `detail-hydration.test.tsx`.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Listing SEO composer — the SINGLE source of truth for the title + meta
// description of the four listing verticals (FBO, flight school, maintenance,
// service provider). Both the rendered <title>/<meta> (via the build*Meta
// functions below) AND the read-only "Search Engine Preview" card in each owner
// portal call this, so the preview can never drift from what crawlers see.
//
// Titles share one shape: `{Name} — {Location}` (the ` | AERO` suffix is added
// later by `withSiteName`/`resolveHead`). Descriptions are compiled from the
// listing's OWN data, keyword-front-loaded, clipped to a meta-safe length, with
// no emojis and no generic filler. These are auto-optimized and not editable.
// ---------------------------------------------------------------------------

export type ListingSeoType =
  | "fbo"
  | "flight_school"
  | "maintenance"
  | "provider"
  | "airport"
  | "experience";

/**
 * Minimal airport fields the SEO composer needs. `Airport` (and the admin
 * airport-edit form) is structurally assignable, so the preview can render off
 * either the saved entity or the in-progress form.
 */
export interface AirportSeoSource {
  code: string;
  name: string;
  city?: string | null;
  state?: string | null;
}

/**
 * Minimal experience/event fields the SEO composer needs. `Event` and
 * `EventSummary` (and the admin event-edit form) are all structurally
 * assignable, so the preview can render off either the saved entity or the
 * in-progress form. `overview` is optional because `EventSummary` omits it.
 */
export interface ExperienceSeoSource {
  slug: string;
  name: string;
  tagline?: string | null;
  overview?: string | null;
  city?: string | null;
  state?: string | null;
}

export type ListingSeoInput =
  | { type: "fbo"; listing: Fbo }
  | { type: "flight_school"; listing: FlightSchoolDetail }
  | { type: "maintenance"; listing: MaintenanceProviderDetail }
  | { type: "provider"; listing: Provider }
  | { type: "airport"; listing: AirportSeoSource }
  | { type: "experience"; listing: ExperienceSeoSource };

export interface ListingSeo {
  /** Title WITHOUT the ` | AERO` suffix (apply `withSiteName` for display). */
  title: string;
  description: string;
}

/**
 * Resolve a human location label for a title, preferring `City, ST` and falling
 * back to the airport name (with code), then the bare code, so an
 * incomplete-data listing never produces a dangling `Name — ,` title.
 */
function locationLabel(parts: {
  city?: string | null;
  state?: string | null;
  airportName?: string | null;
  airportCode?: string | null;
}): string {
  const city = parts.city?.trim();
  const state = parts.state?.trim();
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  if (state) return state;
  const airportName = parts.airportName?.trim();
  const code = parts.airportCode?.trim();
  if (airportName) return code ? `${airportName} (${code})` : airportName;
  if (code) return code;
  return "United States";
}

/**
 * Title location: the human location label with the airport code always appended
 * when one exists (e.g. `Dallas, TX (KDAL)`), unless the label already folds the
 * code in (the no-city fallback) so it is never duplicated.
 */
function titleLocation(parts: {
  city?: string | null;
  state?: string | null;
  airportName?: string | null;
  airportCode?: string | null;
}): string {
  const base = locationLabel(parts);
  const code = parts.airportCode?.trim();
  return code && !base.includes(code) ? `${base} (${code})` : base;
}

/**
 * Sanitize owner-authored prose for use in a meta description: strip emoji,
 * collapse whitespace, and take the first sentence (so a long "About" blurb
 * contributes a clean, keyword-rich clause without blowing the length budget).
 */
function ownerLead(text: string | null | undefined, maxLen = 120): string {
  if (!text) return "";
  const cleaned = text
    .replace(/\p{Extended_Pictographic}/gu, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";
  const match = cleaned.match(/^.*?[.!?](?:\s|$)/);
  let lead = (match ? match[0] : cleaned).trim();
  if (lead.length > maxLen) {
    const slice = lead.slice(0, maxLen - 1);
    const lastSpace = slice.lastIndexOf(" ");
    lead = `${(lastSpace > 40 ? slice.slice(0, lastSpace) : slice).trimEnd()}…`;
  }
  return lead;
}

/** Join non-empty description fragments with single spaces, then clip. */
function composeDescription(...parts: (string | false | null | undefined)[]): string {
  return clip(parts.filter((p): p is string => !!p).join(" "));
}

/**
 * Prefer an APPROVED owner-authored meta description (clipped to a meta-safe
 * length) over the auto-generated one. Only the approved value ever reaches the
 * composer: the public detail schemas expose `metaDescription` as the live
 * (approved) override, while the owner's pending/rejected text stays on the
 * private owner endpoint and never flows here. Falls back to the auto-generated
 * description when the override is absent or whitespace-only.
 */
function withMetaOverride(
  override: string | null | undefined,
  auto: string,
): string {
  const trimmed = override?.replace(/\s+/g, " ").trim();
  return trimmed ? clip(trimmed) : auto;
}

/** Trim, drop blanks, de-dupe (case-insensitive) and take the first `max`. */
function topFeatures(items: readonly string[], max: number): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const v = raw.trim();
    if (!v) continue;
    const key = v.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
    if (out.length >= max) break;
  }
  return out;
}

function composeFboSeo(fbo: Fbo): ListingSeo {
  const fuel = fbo.fuelBrand?.trim()
    ? `${fbo.fuelBrand.trim()} Jet A & Avgas fuel`
    : "Jet A & Avgas fuel";
  const highlights = topFeatures([...fbo.services, ...fbo.amenities], 3);
  const highlightStr = highlights.length ? `, ${highlights.join(", ")}` : "";
  const where = locationLabel(fbo);
  return {
    title: `${fbo.name} — ${titleLocation(fbo)}`,
    description: withMetaOverride(
      fbo.metaDescription,
      composeDescription(
        `${fbo.name} FBO at ${fbo.airportName} (${fbo.airportCode}) in ${where}.`,
        ownerLead(fbo.description),
        `${fuel} pricing${highlightStr}, fees, and pilot reviews.`,
      ),
    ),
  };
}

function composeFlightSchoolSeo(school: FlightSchoolDetail): ListingSeo {
  const programs = topFeatures(school.programCategories, 3);
  const programStr = programs.length ? `${programs.join(", ")} training, ` : "";
  const where = locationLabel(school);
  return {
    title: `${school.name} — ${titleLocation(school)}`,
    description: withMetaOverride(
      school.metaDescription,
      composeDescription(
        `${school.name} flight school at ${school.airportName} (${school.airportCode}) in ${where}.`,
        ownerLead(school.description),
        `${programStr}aircraft fleet, instructors, programs, and student reviews.`,
      ),
    ),
  };
}

function composeMaintenanceSeo(provider: MaintenanceProviderDetail): ListingSeo {
  const disciplines = topFeatures(
    [provider.primaryCategory, ...provider.secondaryCategories],
    3,
  );
  const disciplineStr = disciplines.length
    ? disciplines.join(", ")
    : "Aircraft maintenance";
  const caps = topFeatures(provider.capabilities, 2);
  const capStr = caps.length ? ` ${caps.join(", ")}.` : "";
  const aog = provider.aogAvailable ? "AOG / mobile response available." : "";
  const where = locationLabel(provider);
  return {
    title: `${provider.name} — ${titleLocation(provider)}`,
    description: withMetaOverride(
      provider.metaDescription,
      composeDescription(
        `${provider.name}: ${disciplineStr} for business aircraft at ${provider.airportName} (${provider.airportCode}), ${where}.`,
        ownerLead(provider.description),
        capStr.trim(),
        aog,
      ),
    ),
  };
}

function composeProviderSeo(provider: Provider): ListingSeo {
  const primary = categoryLabel(provider.primaryCategory);
  const location =
    provider.city && provider.state
      ? `${provider.city}, ${provider.state}`
      : coverageSummary(provider);
  const secondary = topFeatures(
    provider.secondaryCategories.map((c) => categoryLabel(c)),
    2,
  );
  const secondaryStr = secondary.length
    ? `Also offering ${secondary.join(", ")}.`
    : "";
  return {
    title: `${provider.name} — ${location}`,
    description: withMetaOverride(
      provider.metaDescription,
      composeDescription(
        `${primary} for business aircraft owners and operators from ${provider.name}.`,
        ownerLead(provider.description),
        secondaryStr,
        `Coverage: ${coverageSummary(provider)}.`,
      ),
    ),
  };
}

function composeAirportSeo(airport: AirportSeoSource): ListingSeo {
  const city = airport.city?.trim();
  const state = airport.state?.trim();
  const where = city && state ? ` in ${city}, ${state}` : "";
  return {
    title: `${airport.name} (${airport.code}) FBOs & Jet A Fuel`,
    description: clip(
      `Compare FBOs, Jet A fuel pricing, runways, and services at ${airport.name} (${airport.code})${where}.`,
    ),
  };
}

function composeExperienceSeo(event: ExperienceSeoSource): ListingSeo {
  return {
    title: event.name,
    description: clip(
      event.tagline ||
        event.overview ||
        `Business aviation operations guide for ${event.name}${event.city ? ` in ${event.city}, ${event.state}` : ""}.`,
    ),
  };
}

/**
 * The dynamic Open Graph (social-share) image path for a listing, root-relative
 * (no origin, no base prefix). This is the SAME path the `build*Meta` functions
 * feed to `og:image`/`twitter:image`, so the portal social-share preview can
 * never drift from the card crawlers/unfurlers render off-site.
 */
export function listingOgImagePath(input: ListingSeoInput): string {
  switch (input.type) {
    case "fbo":
      return `/api/og/fbo/${input.listing.airportCode}/${input.listing.nameSlug}`;
    case "flight_school":
      return `/api/og/flight-school/${input.listing.airportCode}/${input.listing.nameSlug}`;
    case "maintenance":
      return `/api/og/maintenance/${input.listing.airportCode}/${input.listing.nameSlug}`;
    case "provider":
      return `/api/og/provider/${input.listing.slug}`;
    case "airport":
      return `/api/og/airport/${input.listing.code}`;
    case "experience":
      // Experiences have no dynamic OG endpoint (their card uses the event's
      // own hero image); fall back to the site default so the switch stays
      // exhaustive over the shared ListingSeoInput union.
      return "/opengraph.jpg";
  }
}

/**
 * Compose a short, ready-to-paste social post caption for a listing, derived
 * from the SAME auto-optimized SEO source as the live `<title>` and the
 * social-share card. Pure: the title already folds in `{Name} — {Location}`, so
 * the caption stays consistent with the locked SEO model and can never drift.
 * The canonical listing URL is appended on its own line for LinkedIn / X.
 */
export function composeListingSocialPost(
  input: ListingSeoInput,
  url: string,
): string {
  const { title } = composeListingSeo(input);
  return `${title} on ${SITE_NAME}.\n\n${url}`;
}

/**
 * Compose the standardized, auto-optimized title + meta description for a
 * listing. Pure: derives everything from the listing's own fields.
 */
export function composeListingSeo(input: ListingSeoInput): ListingSeo {
  switch (input.type) {
    case "fbo":
      return composeFboSeo(input.listing);
    case "flight_school":
      return composeFlightSchoolSeo(input.listing);
    case "maintenance":
      return composeMaintenanceSeo(input.listing);
    case "provider":
      return composeProviderSeo(input.listing);
    case "airport":
      return composeAirportSeo(input.listing);
    case "experience":
      return composeExperienceSeo(input.listing);
  }
}

/** One question/answer pair for FAQPage structured data. */
interface FaqEntry {
  question: string;
  answer: string;
}

/**
 * Wrap Q/A pairs as FAQPage JSON-LD, or undefined when there is nothing to
 * answer. Answer engines (AI Overviews, ChatGPT search, Perplexity) lean on
 * FAQPage markup to lift direct answers with a citation back to the page.
 */
function faqJsonLd(entries: FaqEntry[]): JsonLd | undefined {
  if (entries.length === 0) return undefined;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entries.map((e) => ({
      "@type": "Question",
      name: e.question,
      acceptedAnswer: { "@type": "Answer", text: e.answer },
    })),
  };
}

const priceUsd = (n: number) => `$${n.toFixed(2)} per gallon`;

/**
 * A short, answer-shaped lead paragraph for the FBO detail page — a direct,
 * factual sentence an answer engine can quote verbatim. Built only from data
 * the listing actually has; missing facts are simply omitted.
 */
export function composeFboAnswerSummary(fbo: Fbo): string {
  const parts: string[] = [
    `${fbo.name} is an FBO at ${fbo.airportName} (${fbo.airportCode}) in ${fbo.city}, ${fbo.state}.`,
  ];
  const fuel: string[] = [];
  if (fbo.jetAPrice != null) fuel.push(`Jet A is ${priceUsd(fbo.jetAPrice)}`);
  if (fbo.avgasPrice != null) fuel.push(`Avgas is ${priceUsd(fbo.avgasPrice)}`);
  if (fuel.length > 0) parts.push(`${fuel.join(" and ")}.`);
  const highlights = topFeatures([...fbo.services, ...fbo.amenities], 4);
  if (highlights.length > 0) {
    parts.push(`Highlights include ${highlights.join(", ")}.`);
  }
  return parts.join(" ");
}

/**
 * Data-derived FAQ for an FBO. Every answer is built ONLY from fields the
 * listing actually has — a missing fact simply omits the question, it is
 * never fabricated (matches the profile-search "absence = no entry" rule).
 */
const UTC_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Deterministic UTC date formatter ("July 25, 2026"). Locale/timezone-default
 * formatting differs between the SSR server and the visitor's browser, which
 * would make the FAQ JSON-LD mismatch on hydration near day boundaries.
 */
function formatUtcDate(iso: string): string {
  const d = new Date(iso);
  return `${UTC_MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

function buildFboFaq(fbo: Fbo): FaqEntry[] {
  const at = `${fbo.name} at ${fbo.airportCode}`;
  const entries: FaqEntry[] = [];
  if (fbo.jetAPrice != null) {
    const asOf = fbo.fuelUpdatedAt
      ? ` (as of ${formatUtcDate(fbo.fuelUpdatedAt)})`
      : "";
    entries.push({
      question: `What is the Jet A fuel price at ${at}?`,
      answer: `Jet A is ${priceUsd(fbo.jetAPrice)} at ${at}${asOf}.`,
    });
  }
  if (fbo.avgasPrice != null) {
    entries.push({
      question: `What is the Avgas (100LL) price at ${at}?`,
      answer: `Avgas is ${priceUsd(fbo.avgasPrice)} at ${at}.`,
    });
  }
  if (fbo.amenities.length > 0) {
    entries.push({
      question: `What amenities does ${fbo.name} offer?`,
      answer: `${at} offers ${fbo.amenities.join(", ")}.`,
    });
  }
  if (fbo.services.length > 0) {
    entries.push({
      question: `What services does ${fbo.name} provide?`,
      answer: `${at} provides ${fbo.services.join(", ")}.`,
    });
  }
  if (fbo.phone) {
    entries.push({
      question: `How do I contact ${fbo.name}?`,
      answer: `${at} can be reached at ${fbo.phone}.`,
    });
  }
  return entries;
}

export function buildFboMeta(fbo: Fbo, ctx: SeoContext): MetaInput {
  const hero = fbo.photos[0];
  const canonicalPath = fboDetailPath(fbo.airportCode, fbo.nameSlug);
  const { title, description } = composeListingSeo({ type: "fbo", listing: fbo });
  const business: JsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: fbo.name,
    image: hero ?? undefined,
    url: canonicalPath,
    telephone: fbo.phone ?? undefined,
    address: {
      "@type": "PostalAddress",
      streetAddress: fbo.address ?? undefined,
      addressLocality: fbo.city,
      addressRegion: fbo.state,
      addressCountry: fbo.country ?? "US",
    },
  };
  const faq = faqJsonLd(buildFboFaq(fbo));
  return {
    title,
    description,
    canonicalPath,
    image: ctx.abs(`/api/og/fbo/${fbo.airportCode}/${fbo.nameSlug}`),
    jsonLd: faq ? [business, faq] : business,
  };
}

export function buildAirportMeta(airport: Airport, ctx: SeoContext): MetaInput {
  const { title, description } = composeListingSeo({
    type: "airport",
    listing: airport,
  });
  return {
    title,
    description,
    canonicalPath: airportDetailPath(airport.code),
    image: ctx.abs(`/api/og/airport/${airport.code}`),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Airport",
      name: airport.name,
      iataCode: airport.faaIdent ?? undefined,
      icaoCode: airport.code,
      address: {
        "@type": "PostalAddress",
        addressLocality: airport.city,
        addressRegion: airport.state,
        addressCountry: airport.country ?? "US",
      },
    },
  };
}

export function buildFlightSchoolMeta(
  school: FlightSchoolDetail,
  ctx: SeoContext,
): MetaInput {
  const hero = school.photos[0];
  const canonicalPath = flightSchoolDetailPath(
    school.airportCode,
    school.nameSlug,
  );
  const { title, description } = composeListingSeo({
    type: "flight_school",
    listing: school,
  });
  return {
    title,
    description,
    canonicalPath,
    image: ctx.abs(
      `/api/og/flight-school/${school.airportCode}/${school.nameSlug}`,
    ),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      name: school.name,
      image: hero ?? undefined,
      url: canonicalPath,
      telephone: school.phone ?? undefined,
      address: {
        "@type": "PostalAddress",
        streetAddress: school.address ?? undefined,
        addressLocality: school.city,
        addressRegion: school.state,
        addressCountry: school.country ?? "US",
      },
    },
  };
}

export function buildMaintenanceMeta(
  provider: MaintenanceProviderDetail,
  ctx: SeoContext,
): MetaInput {
  const hero = provider.photos[0];
  const canonicalPath = maintenanceDetailPath(
    provider.airportCode,
    provider.nameSlug,
  );
  const { title, description } = composeListingSeo({
    type: "maintenance",
    listing: provider,
  });
  return {
    title,
    description,
    canonicalPath,
    image: ctx.abs(
      `/api/og/maintenance/${provider.airportCode}/${provider.nameSlug}`,
    ),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: provider.name,
      image: hero ?? undefined,
      url: canonicalPath,
      telephone: provider.phone ?? undefined,
      address: {
        "@type": "PostalAddress",
        streetAddress: provider.address ?? undefined,
        addressLocality: provider.city,
        addressRegion: provider.state,
        addressCountry: provider.country ?? "US",
      },
    },
  };
}

export function buildProviderMeta(
  provider: Provider,
  ctx: SeoContext,
): MetaInput {
  const hero = provider.photos[0];
  const canonicalPath = providerDetailPath(provider.slug);
  const { title, description } = composeListingSeo({
    type: "provider",
    listing: provider,
  });
  return {
    title,
    description,
    canonicalPath,
    image: ctx.abs(`/api/og/provider/${provider.slug}`),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: provider.name,
      image: hero ?? undefined,
      url: canonicalPath,
      telephone: provider.phone ?? undefined,
      parentOrganization: provider.parentOrganization ?? undefined,
      address: {
        "@type": "PostalAddress",
        streetAddress: provider.address ?? undefined,
        addressLocality: provider.city ?? undefined,
        addressRegion: provider.state ?? undefined,
        addressCountry: provider.country ?? "US",
      },
    },
  };
}

export function buildExperienceMeta(
  event: Event,
  ctx: SeoContext,
): MetaInput {
  const { title, description } = composeListingSeo({
    type: "experience",
    listing: event,
  });
  return {
    title,
    description,
    canonicalPath: experienceDetailPath(event.slug),
    image: event.heroImageUrl ? ctx.resolveImg(event.heroImageUrl) : null,
    noindex: event.status !== "published",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Event",
      name: event.name,
      startDate: event.startDate ?? undefined,
      endDate: event.endDate ?? undefined,
      eventStatus: "https://schema.org/EventScheduled",
      location: event.venueName
        ? {
            "@type": "Place",
            name: event.venueName,
            address:
              [event.city, event.state].filter(Boolean).join(", ") || undefined,
          }
        : undefined,
    },
  };
}

export function buildPrivacyPolicyMeta(policy: PolicyDocumentDetail): MetaInput {
  return {
    title: policy.title,
    description: policy.summary || undefined,
    canonicalPath: `/privacy/${policy.slug}`,
  };
}

export function buildCmsPageMeta(
  page: CmsPage,
  slug: string,
  opts?: { preview?: boolean },
): MetaInput {
  return {
    title: page.seoTitle || page.title || SITE_NAME,
    description: page.seoDescription || undefined,
    image: page.seoImage || undefined,
    canonicalPath: page.canonicalUrl || cmsPageRealRoute(slug) || `/p/${slug}`,
    noindex:
      page.noindex || Boolean(opts?.preview) || Boolean(page.templateEntityType),
  };
}
